/**
 * Product Repository Implementation
 *
 * Repository implementation for product data access following clean architecture principles.
 * Implements the ProductRepository interface using the existing API data source.
 *
 * @module ProductRepositoryImpl
 */

import {ProductRepository} from './ProductRepository';
import {Product} from '../models/product.model';
import {ProductAPIDataSourceCombined} from '../api';
import {Tag} from '../models/tag.model';
import {BrandDTO} from '../models/brand.dto';
import {ProductType} from '../models/productType.model';
import {Pagination} from '../../../Data/types/BaseResponse';
import {mapProductDTOToModel} from '../mappers/product.mapper';
import {AppError} from '../../../Data/config/AppError';

export class ProductRepositoryImpl implements ProductRepository {
  constructor (private apiDataSource: typeof ProductAPIDataSourceCombined) {}

  /**
   * Create a new product
   * @param formData - Form data for product creation
   * @returns Promise<Product> - Created product
   */
  async createProduct (formData: FormData): Promise<Product> {
    try {
      const productDTO = await this.apiDataSource.create(formData);
      return mapProductDTOToModel(productDTO);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  /**
   * Get product by ID
   * @param id - Product ID
   * @returns Promise<Product | null> - Product or null if not found
   */
  async getProductById (id: number): Promise<Product | null> {
    try {
      const productDTO = await this.apiDataSource.getById(id);
      return mapProductDTOToModel(productDTO);
    } catch (error) {
      // Return null for not found, but throw for other errors
      if (error instanceof AppError && error.statusCode === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get all products
   * @param filters - Optional filters
   * @returns Promise<Product[]> - Array of products
   */
  async getProducts (filters?: any): Promise<Product[]> {
    try {
      const products = await this.apiDataSource.getAll(filters);
      return products;
    } catch (error) {
      // Don't handle error here - just throw it so it can be handled at the component level
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  /**
   * Update product
   * @param id - Product ID
   * @param formData - Updated form data
   * @returns Promise<Product> - Updated product
   */
  async updateProduct (id: number, formData: FormData): Promise<Product> {
    try {
      const productDTO = await this.apiDataSource.update(id, formData);
      return mapProductDTOToModel(productDTO);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  /**
   * Get all products with pagination
   * @param page - Page number
   * @param limit - Items per page
   * @returns Promise<{ items: Product[]; pagination?: Pagination }> - Paginated products
   */
  async getAll (page?: number, limit?: number): Promise<{ items: Product[]; pagination?: Pagination }> {
    try {
      const result = await this.apiDataSource.getAll(page, limit);
      // Map DTOs to domain models
      const mappedProducts = result.items.map(mapProductDTOToModel);
      return {
        items: mappedProducts,
        pagination: result.pagination
      };
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  /**
   * Get all tags
   * @returns Promise<Tag[]> - Array of tags
   */
  async getTags (): Promise<Tag[]> {
    try {
      const tags = await this.apiDataSource.getTags();
      return tags;
    } catch (error) {
      // Don't handle error here - just throw it so it can be handled at the component level
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  /**
   * Get all brands
   * @returns Promise<BrandDTO[]> - Array of brands
   */
  async getBrands (): Promise<BrandDTO[]> {
    try {
      const brands = await this.apiDataSource.getBrands();
      return brands;
    } catch (error) {
      // Don't handle error here - just throw it so it can be handled at the component level
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  /**
   * Get all product types
   * @returns Promise<ProductType[]> - Array of product types
   */
  async getProductTypes (): Promise<ProductType[]> {
    try {
      const productTypes = await this.apiDataSource.getProductTypes();
      return productTypes;
    } catch (error) {
      // Don't handle error here - just throw it so it can be handled at the component level
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  /**
   * Delete product
   * @param id - Product ID
   * @returns Promise<void>
   */
  async deleteProduct (id: number): Promise<void> {
    try {
      await this.apiDataSource.deleteProduct(id);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }
}
